import { useCallback } from "react";
import { getRecipe } from "./productRecipeApi";
import { RecipeRuntime, fromRecipe } from "./productRecipeEditor";
import { readRecipeDraft } from "./useRecipeDraftRecovery";
export default function useRecipeResource(
  {
    productId,
    productSellingUnit,
    alive,
    busy,
    nextKey,
    patch,
    fail,
  }: RecipeRuntime,
  storageKey: string,
  resetSearch: () => void
) {
  const load = useCallback(
    async (restoreDraft = false) => {
      if (busy.current) return;
      busy.current = true;
      patch({ loading: true, error: "", errors: {}, conflict: false });
      resetSearch();
      try {
        const result = await getRecipe(productId);
        if (!alive.current) return;
        const fresh = fromRecipe(result.recipe, productSellingUnit);
        const restored =
          restoreDraft && result.can_manage
            ? readRecipeDraft(storageKey)
            : null;
        const draft = restored?.draft ?? fresh;
        const conflict = Boolean(
          restored && restored.version !== (result.recipe?.version ?? null)
        );
        nextKey.current = Math.max(0, ...draft.rows.map((row) => row.key + 1));
        patch({
          recipe: result.recipe,
          version: restored ? restored.version : result.recipe?.version ?? null,
          draft,
          baseline: JSON.stringify(fresh),
          canManage: result.can_manage,
          productAvailable: result.product_available,
          editing: Boolean(
            result.recipe ||
              restored ||
              (result.can_manage && result.product_available)
          ),
          loaded: true,
          conflict,
          notice: restored ? "Unsaved recipe draft restored in this tab." : "",
          error: conflict
            ? "The saved recipe changed while you were away. Your draft is preserved; reload the saved recipe before editing."
            : "",
        });
      } catch (cause) {
        fail(cause);
      } finally {
        busy.current = false;
        if (alive.current) patch({ loading: false });
      }
    },
    [
      productId,
      productSellingUnit,
      storageKey,
      fail,
      patch,
      resetSearch,
      alive,
      busy,
      nextKey,
    ]
  );
  return load;
}
